<template>
  <div class="stackability-card">
    <h2 class="card-title">
      Select how your shipments should be quoted by default regarding cargo stackability.
    </h2>

    <div class="card-content">
      <p>
        <strong>Stackable:</strong>
        Freight that can safely have similar freight placed on top during standard LTL handling.
        Stackable freight may help with operational handling, but does not guarantee reduced
        space or cost.
      </p>

      <p>
        <strong>Non-Stackable:</strong>
        Freight that cannot have any freight placed on top and requires dedicated vertical space.
        May result in additional charges.
      </p>

      <p class="highlight">
        You can still change this option manually for individual quotes if needed.
      </p>
    </div>

    <div class="options">
      <label class="checkbox-wrapper">
        <input
          type="checkbox"
          :checked="stackable === true"
          @change="selectStackable(true)"
        >
        <span>Stackable</span>
      </label>

      <label class="checkbox-wrapper">
        <input
          type="checkbox"
          :checked="stackable === false"
          @change="selectStackable(false)"
        >
        <span>Non-Stackable</span>
      </label>
    </div>

    <p class="disclaimer">
      Final charges are determined by the carrier based on actual handling and space used.
      Incorrect stackability selection based on freight characteristics, packaging, or loading
      conditions at origin may result in additional charges, damages or claims.
    </p>

    <div class="actions">
      <button
        class="save-btn"
        @click="save"
        v-loading="loading"
      >
        Save
      </button>
    </div>
  </div>
</template>

<script>
import { errorMessage, generalMessage } from '../../../../utils/ErrorMessages';

export default {
  name: 'StackabilityCard',

  data() {
    return {
      stackable: null,
      loading: false
    };
  },

  created() {
    this.getStackableInfo();
  },

  methods: {
    selectStackable(value) {
      if (this.stackable === value) {
        this.stackable = null;
      } else {
        this.stackable = value;
      }
    },
    getStackableInfo() {
      this.stackable = JSON.parse(localStorage.getItem("cargo-stackability"));
    },
    async save() {
      this.loading = true;
      try {
        const tokenInfo = this.$store.getters["login/getTokenInfo"];
        const payload = {
          id: tokenInfo.account_id,
          stackable: this.stackable,
        };
        await this.$store.dispatch("account/updateSettingsStackable", payload);
        generalMessage('The parameter was successfully modified');
      } catch (error) {
        errorMessage(error);
      } finally {
        this.loading = false;
        localStorage.setItem("cargo-stackability", this.stackable);
        this.$router.push({ name: "Settings" });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/_variables' as *;

.stackability-card {
  width: 720px;
  max-width: 95%;
  background: $color-white;
  border-radius: 12px;
  padding: 40px 48px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  margin: 40px auto;
  font-family: $font-family-portal;
  color: $color-background-request;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 24px;
  color: $color-background-request;
}

.card-content p {
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 16px;
  text-align: start;
  color: $color-background-request;
}

.card-content strong {
  color: $color-background-request;
}

.highlight {
  font-weight: 800;
  color: $color-background-request;
  margin-top: 12px;
}

.options {
  display: flex;
  justify-content: center;
  gap: 80px;
  margin: 32px 0;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: $color-background-request;
  cursor: pointer;
}

.checkbox-wrapper input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid $color-sidebar-selected-option;
  border-radius: 4px;
  background-color: $color-white;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

.checkbox-wrapper input[type="checkbox"]:checked {
  background-color: $color-sidebar-selected-option;
  border-color: $color-sidebar-selected-option;
}

.checkbox-wrapper input[type="checkbox"]::after {
  content: "";
  position: absolute;
  width: 4px;
  height: 8px;
  border: solid $color-white;
  border-width: 0 2px 2px 0;
  top: 2px;
  left: 5px;
  transform: rotate(45deg) scale(0);
  transition: transform 0.15s ease-in-out;
}

.checkbox-wrapper input[type="checkbox"]:checked::after {
  transform: rotate(45deg) scale(1);
}

.disclaimer {
  font-size: 12px;
  color: $color-background-request;
  line-height: 1.5;
  margin-top: 16px;
  font-style: italic;
}

.actions {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

.save-btn {
  background-color: $color-sidebar-selected-option;;
  color: white;
  border: none;
  padding: 10px 32px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
}

.save-btn:hover {
  background-color: $color-primary-trigger-button-hover;
}

.save-btn:active {
  transform: scale(0.98);
}

</style>
